import { useEffect, useRef } from "react";
import type { UIMessage } from "@ai-sdk/react";
import type { AvatarHandle } from "../components/agent-chat/types";
import { buildMarcusVoiceCue } from "../services/marcusVoiceCue";

interface UseMarcusVoiceRepliesParams {
  enabled: boolean;
  activeMessages: UIMessage[];
  status: string;
  avatarRef: React.RefObject<AvatarHandle | null>;
  avatarMuted: boolean;
  turnEpoch: number;
}

function extractMessageText(message: UIMessage): string {
  return message.parts
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("");
}

export function useMarcusVoiceReplies({
  enabled,
  activeMessages,
  status,
  avatarRef,
  avatarMuted,
  turnEpoch,
}: UseMarcusVoiceRepliesParams) {
  const seededRef = useRef(false);
  const seenUserIdsRef = useRef<Set<string>>(new Set());
  const spokenAssistantIdsRef = useRef<Set<string>>(new Set());
  const pendingUserIdRef = useRef<string | null>(null);
  const pendingEpochRef = useRef<number | null>(null);
  const lastTurnEpochRef = useRef(turnEpoch);

  useEffect(() => {
    if (!seededRef.current) {
      seededRef.current = true;
      for (const message of activeMessages) {
        if (message.role === "user") seenUserIdsRef.current.add(message.id);
        if (message.role === "assistant") spokenAssistantIdsRef.current.add(message.id);
      }
    }

    if (!enabled) {
      pendingUserIdRef.current = null;
      pendingEpochRef.current = null;
      return;
    }

    if (turnEpoch !== lastTurnEpochRef.current) {
      lastTurnEpochRef.current = turnEpoch;
      pendingUserIdRef.current = null;
      pendingEpochRef.current = null;
      if (turnEpoch > 0) avatarRef.current?.clearQueue();
    }

    for (const message of activeMessages) {
      if (message.role !== "user") continue;
      if (seenUserIdsRef.current.has(message.id)) continue;
      seenUserIdsRef.current.add(message.id);
      if (turnEpoch > 0) {
        pendingUserIdRef.current = message.id;
        pendingEpochRef.current = turnEpoch;
      }
    }

    const pendingUserId = pendingUserIdRef.current;
    if (!pendingUserId || pendingEpochRef.current !== turnEpoch || status !== "ready") return;

    const userIndex = activeMessages.findIndex((message) => message.id === pendingUserId);
    if (userIndex < 0) return;

    const assistantsAfterUser = activeMessages
      .slice(userIndex + 1)
      .filter((message) => message.role === "assistant");
    const assistant = assistantsAfterUser[assistantsAfterUser.length - 1];
    if (!assistant || spokenAssistantIdsRef.current.has(assistant.id)) return;

    spokenAssistantIdsRef.current.add(assistant.id);
    pendingUserIdRef.current = null;
    pendingEpochRef.current = null;

    if (avatarMuted) return;

    const cue = buildMarcusVoiceCue(extractMessageText(assistant));
    if (cue) avatarRef.current?.speak(cue);
  }, [activeMessages, avatarMuted, avatarRef, enabled, status, turnEpoch]);
}
